/**
 * Client-side sort for variant-as-product sections.
 * Intercepts the sorting-filter-component before it calls sectionRenderer
 * and instead sorts the cards in the DOM directly.
 */
(function () {
  function getSortValue(card, sortBy) {
    if (sortBy.indexOf('price') !== -1) {
      const priceEl = card.querySelector('.price-item--sale, .price-item--regular');
      if (!priceEl) return 0;
      return parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) || 0;
    }
    if (sortBy.indexOf('title') !== -1) {
      const titleEl = card.querySelector('.variant-card__title');
      return titleEl ? titleEl.textContent.trim().toLowerCase() : '';
    }
    // created, manual, best-selling — fall back to original DOM order
    return 0;
  }

  function sortCards(grid, sortBy) {
    const cards = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    if (!cards.length) return;

    const descending = sortBy.indexOf('desc') !== -1 || sortBy === 'price-descending';

    cards.sort(function (a, b) {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return descending ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }
      return descending ? bVal - aVal : aVal - bVal;
    });

    const fragment = document.createDocumentFragment();
    cards.forEach(function (card) { fragment.appendChild(card); });
    grid.appendChild(fragment);
  }

  function patchSortingComponent(section) {
    const grid = section.querySelector('[ref="grid"]');
    if (!grid) return;

    const sortingComponents = section.querySelectorAll('sorting-filter-component');

    sortingComponents.forEach(function (component) {
      // Store original method
      const original = component.updateFilterAndSorting?.bind(component);

      component.updateFilterAndSorting = function (data, event) {
        // Get the selected sort value
        let sortBy = null;

        // Desktop: radio inputs
        const checkedInput = component.querySelector('input[name="sort_by"]:checked');
        if (checkedInput) sortBy = checkedInput.value;

        // Mobile: select element
        if (!sortBy) {
          const select = component.querySelector('select[name="sort_by"]');
          if (select) sortBy = select.value;
        }

        // Also check event target directly
        if (!sortBy && event && event.target) {
          if (event.target.name === 'sort_by') sortBy = event.target.value;
        }

        if (sortBy) {
          sortCards(grid, sortBy);

          // Update URL without triggering sectionRenderer
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortBy);
          history.pushState({}, '', url.toString());

          // Close the details dropdown (replicating original behavior)
          const details = component.querySelector('details');
          if (details) details.open = false;

          // Update facet status text (replicating original behavior)
          if (event && event.target instanceof HTMLSelectElement) {
            const detailsEl = component.querySelector('details');
            if (detailsEl) {
              const facetStatus = detailsEl.querySelector('facet-status-component');
              if (facetStatus) {
                facetStatus.textContent =
                  event.target.value !== detailsEl.dataset.defaultSortBy
                    ? event.target.dataset.optionName ?? ''
                    : '';
              }
            }
          }
        } else {
          // Fallback to original if we can't determine sort value
          if (original) original(data, event);
        }
      };
    });
  }

  function init() {
    // Find all variant-as-product results-list sections
    const sections = document.querySelectorAll('results-list');

    sections.forEach(function (section) {
      // Only patch sections that have variant cards
      if (!section.querySelector('.variant-card__title')) return;
      patchSortingComponent(section);
    });
  }

  // Wait for custom elements to be defined before patching
  Promise.all([
    customElements.whenDefined('sorting-filter-component'),
    customElements.whenDefined('results-list'),
  ]).then(function () {
    // Small delay to ensure connectedCallback has run
    requestAnimationFrame(init);
  });
})();

/**
 * Hover to second image behavior for variant cards
 */
(function () {
  function initHover(section) {
    section.addEventListener('mouseenter', function (e) {
      const gallery = e.target.closest('.variant-card__gallery');
      if (!gallery) return;
      const hoverSrc = gallery.dataset.hoverImage;
      if (!hoverSrc) return;
      const img = gallery.querySelector('.variant-card__img');
      if (img) img.src = hoverSrc;
    }, true);

    section.addEventListener('mouseleave', function (e) {
      const gallery = e.target.closest('.variant-card__gallery');
      if (!gallery) return;
      const primarySrc = gallery.dataset.primaryImage;
      if (!primarySrc) return;
      const img = gallery.querySelector('.variant-card__img');
      if (img) img.src = primarySrc;
    }, true);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('results-list').forEach(function (section) {
      if (!section.querySelector('.variant-card__title')) return;
      initHover(section);
    });
  });
})();

/**
 * Prevent sort accordion from opening by default on mobile
 * inside variant-as-product sections
 */
(function () {
  function closeMobileSortAccordions(section) {
    if (!section.querySelector('.variant-card__title')) return;

    section.querySelectorAll('accordion-custom').forEach(function (accordion) {
      const original = accordion._setDefaultOpenState || accordion['#setDefaultOpenState'];

      // Override connectedCallback behavior by closing after it runs
      requestAnimationFrame(function () {
        const isMobile = window.innerWidth < 750;
        if (isMobile) {
          const details = accordion.querySelector('details');
          if (details) details.open = false;
        }
      });

      // Also handle resize
      window.addEventListener('resize', function () {
        const isMobile = window.innerWidth < 750;
        if (isMobile) {
          const details = accordion.querySelector('details');
          if (details && details.open && !details.dataset.userOpened) {
            details.open = false;
          }
        }
      });

      // Track user-initiated opens
      const details = accordion.querySelector('details');
      const summary = details && details.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function () {
          if (details.open) {
            delete details.dataset.userOpened;
          } else {
            details.dataset.userOpened = 'true';
          }
        });
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('results-list').forEach(closeMobileSortAccordions);
  });
})();